import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { localDateStr } from '../utils/date'

export default function DashboardHome({ profile, setPage }) {
  const isAdmin = profile.role === 'admin'
  const [stats, setStats] = useState({ pending:0, deliveryOut:0, lowStock:0, asMonth:0, myLeaveRemain:0 })
  const [recentLeave, setRecentLeave] = useState([])
  const [recentAs, setRecentAs] = useState([])

  // 투두
  const [todos, setTodos] = useState([])
  const [newTodo, setNewTodo] = useState('')
  const [newPriority, setNewPriority] = useState(false)

  useEffect(() => {
    fetchStats()
    fetchRecentLeave()
    fetchRecentAs()
    fetchTodos()
  }, [])

  async function fetchStats() {
    const monthStart = new Date(); monthStart.setDate(1)
    const monthStartStr = localDateStr(monthStart)
    const [pending, deliveryOut, materials, asMonth, me] = await Promise.all([
      supabase.from('leave_requests').select('id', { count:'exact', head:true }).eq('status','pending'),
      supabase.from('deliveries').select('id', { count:'exact', head:true }).eq('status','out'),
      supabase.from('materials').select('qty,safe_qty'),
      supabase.from('as_records').select('id', { count:'exact', head:true }).gte('date', monthStartStr),
      supabase.from('profiles').select('leave_quota,leave_used').eq('id', profile.id).single(),
    ])
    const lowStock = (materials.data || []).filter(m => m.qty <= m.safe_qty).length
    const myLeaveRemain = me.data ? (me.data.leave_quota || 0) - (me.data.leave_used || 0) : 0
    setStats({
      pending: pending.count || 0,
      deliveryOut: deliveryOut.count || 0,
      lowStock,
      asMonth: asMonth.count || 0,
      myLeaveRemain,
    })
  }

  async function fetchRecentLeave() {
    let query = supabase.from('leave_requests').select('*').order('created_at', { ascending:false }).limit(3)
    if (!isAdmin) query = query.eq('emp_id', profile.id)
    const { data } = await query
    if (data) setRecentLeave(data)
  }

  async function fetchRecentAs() {
    const { data } = await supabase.from('as_records').select('*').order('created_at', { ascending:false }).limit(3)
    if (data) setRecentAs(data)
  }

  async function fetchTodos() {
    // 전 직원 공유 목록
    const { data, error } = await supabase.from('todos').select('*')
      .order('done').order('priority', { ascending:false }).order('created_at')
    if (error) { console.error('todos 조회:', error.message); return }
    if (data) setTodos(data)
  }

  async function addTodo() {
    const t = newTodo.trim()
    if (!t) return
    const { error } = await supabase.from('todos').insert({
      owner_id: profile.id, owner_name: profile.name, title: t, priority: newPriority ? 1 : 0,
    })
    if (error) { alert('추가 실패: ' + error.message + '\n\ntodos 공유 설정을 했는지 확인하세요 (supabase/sql/todos_shared.sql 실행)'); return }
    setNewTodo(''); setNewPriority(false); fetchTodos()
  }

  async function toggleTodo(t) {
    await supabase.from('todos').update({
      done: !t.done,
      done_at: !t.done ? new Date().toISOString() : null,
      done_by: !t.done ? profile.name : null,
    }).eq('id', t.id)
    fetchTodos()
  }

  async function togglePriority(t) {
    await supabase.from('todos').update({ priority: t.priority ? 0 : 1 }).eq('id', t.id)
    fetchTodos()
  }

  async function deleteTodo(t) {
    await supabase.from('todos').delete().eq('id', t.id)
    fetchTodos()
  }

  async function clearDone() {
    const doneIds = todos.filter(t => t.done).map(t => t.id)
    if (!doneIds.length) return
    if (!window.confirm(`완료한 할 일 ${doneIds.length}개를 목록에서 지울까요?`)) return
    await supabase.from('todos').delete().in('id', doneIds)
    fetchTodos()
  }

  const statusLabel = s => ({ pending:'대기중', approved:'승인', rejected:'반려' }[s] || s)
  const statusColor = s => ({ pending:'#c0601a', approved:'#2d5a3d', rejected:'#b83030' }[s])
  const statusBg = s => ({ pending:'#fff3e0', approved:'#e8f2eb', rejected:'#fdeaea' }[s])

  const undoneCount = todos.filter(t => !t.done).length
  const doneCount = todos.filter(t => t.done).length

  // 통계 카드 — 역할별 구성
  const cards = isAdmin
    ? [
        { label:'연차 승인 대기', value:stats.pending, unit:'건', color:'#c0601a', bg:'#fff3e0', icon:'📅', page:'leave' },
        { label:'출고 대기(인도 전)', value:stats.deliveryOut, unit:'건', color:'#1e4a8c', bg:'#e8eef8', icon:'🚚', page:'delivery' },
        { label:'자재 부족', value:stats.lowStock, unit:'종', color:'#b83030', bg:'#fdeaea', icon:'🪨', page:'inventory' },
        { label:'이번 달 AS', value:stats.asMonth, unit:'건', color:'#2d5a3d', bg:'#e8f2eb', icon:'🔧', page:'as' },
      ]
    : [
        { label:'내 잔여 연차', value:stats.myLeaveRemain, unit:'일', color:'#2d5a3d', bg:'#e8f2eb', icon:'📅', page:'leave' },
        { label:'출고 대기(인도 전)', value:stats.deliveryOut, unit:'건', color:'#1e4a8c', bg:'#e8eef8', icon:'🚚', page:'delivery' },
        { label:'자재 부족', value:stats.lowStock, unit:'종', color:'#b83030', bg:'#fdeaea', icon:'🪨', page:'inventory' },
        { label:'이번 달 AS', value:stats.asMonth, unit:'건', color:'#2d5a3d', bg:'#e8f2eb', icon:'🔧', page:'as' },
      ]

  return (
    <div>
      {/* 환영 */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:18, fontWeight:700 }}>안녕하세요, {profile.name}님 👋</div>
        <div style={{ fontSize:13, color:'#a09e99', marginTop:4 }}>
          {new Date().toLocaleDateString('ko-KR', { year:'numeric', month:'long', day:'numeric', weekday:'short' })}
        </div>
      </div>

      {/* 통계 카드 2x2 */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24 }}>
        {cards.map((s,i) => (
          <div key={i} onClick={() => setPage(s.page)}
            style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:12, padding:'16px', cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:22, marginBottom:8 }}>{s.icon}</div>
            <div style={{ fontSize:24, fontWeight:700, color:s.color }}>
              {s.value}<span style={{ fontSize:13, fontWeight:500, color:'#a09e99', marginLeft:2 }}>{s.unit}</span>
            </div>
            <div style={{ fontSize:11, color:'#a09e99', marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ───── 오늘 할 일 (투두) ───── */}
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:12, marginBottom:16, overflow:'hidden' }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
          <span style={{ fontWeight:700, fontSize:14 }}>✅ 함께 할 일
            <span style={{ fontSize:12, fontWeight:500, color:'#a09e99', marginLeft:8 }}>
              {undoneCount > 0 ? `${undoneCount}개 남음` : '모두 완료!'}
            </span>
          </span>
          {doneCount > 0 && (
            <button type="button" onClick={clearDone}
              style={{ fontSize:11, color:'#a09e99', border:'none', background:'transparent', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>완료 {doneCount}개 정리</button>
          )}
        </div>

        {/* 입력 — 입력칸 한 줄, 아래 [긴급 토글][추가] */}
        <div style={{ padding:'12px 16px', borderBottom:'1px solid #f0eeea' }}>
          <input value={newTodo} onChange={e => setNewTodo(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addTodo()}
            placeholder="할 일을 입력하세요"
            style={{ width:'100%', padding:'10px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box', marginBottom:8 }} />
          <div style={{ display:'flex', gap:8 }}>
            <button type="button" onClick={() => setNewPriority(p => !p)}
              style={{
                flex:1, padding:'9px 0', borderRadius:8, cursor:'pointer', fontSize:13, fontFamily:'inherit', fontWeight:600,
                border: newPriority ? '2px solid #b83030' : '1px solid #e5e3de',
                background: newPriority ? '#fdeaea' : '#fff',
                color: newPriority ? '#b83030' : '#a09e99',
              }}>
              🔴 긴급 {newPriority ? 'ON' : 'OFF'}
            </button>
            <button type="button" onClick={addTodo}
              style={{ flex:2, padding:'9px 0', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:14, fontWeight:600 }}>
              + 추가
            </button>
          </div>
        </div>

        {/* 목록 */}
        {todos.length === 0 ? (
          <div style={{ padding:30, textAlign:'center', color:'#a09e99', fontSize:13 }}>할 일을 추가해 보세요</div>
        ) : todos.map(t => (
          <div key={t.id} style={{ padding:'11px 16px', borderBottom:'1px solid #f0eeea', display:'flex', alignItems:'flex-start', gap:12 }}>
            {/* 체크박스 */}
            <button type="button" onClick={() => toggleTodo(t)}
              style={{
                width:22, height:22, borderRadius:'50%', flexShrink:0, cursor:'pointer', marginTop:1,
                border: t.done ? 'none' : '2px solid #c8c6c0',
                background: t.done ? '#2d5a3d' : '#fff',
                color:'#fff', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center',
              }}>
              {t.done ? '✓' : ''}
            </button>
            {/* 제목 + 작성자/완료자 */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{
                fontSize:14, lineHeight:1.4,
                textDecoration: t.done ? 'line-through' : 'none',
                color: t.done ? '#b8b6b0' : '#1a1916',
              }}>
                {!t.done && t.priority > 0 && <span style={{ color:'#b83030', marginRight:5 }}>🔴</span>}
                {t.title}
              </div>
              <div style={{ fontSize:11, marginTop:3 }}>
                {t.owner_name && <span style={{ color:'#a09e99' }}>{t.owner_name} 등록</span>}
                {t.done && t.done_by && <span style={{ color:'#4a8c60' }}> · {t.done_by} 완료</span>}
              </div>
            </div>
            {/* 액션 */}
            {!t.done && (
              <button type="button" onClick={() => togglePriority(t)} title="긴급 전환"
                style={{ border:'none', background:'transparent', cursor:'pointer', fontSize:13, opacity: t.priority ? 1 : 0.35, flexShrink:0, marginTop:1 }}>🔴</button>
            )}
            <button type="button" onClick={() => deleteTodo(t)}
              style={{ border:'none', background:'transparent', color:'#cfcdc8', cursor:'pointer', fontSize:14, flexShrink:0, marginTop:1 }}>✕</button>
          </div>
        ))}
      </div>

      {/* 최근 연차 */}
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:12, marginBottom:16, overflow:'hidden' }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontWeight:700, fontSize:14 }}>{isAdmin ? '최근 연차 신청' : '내 연차'}</span>
          <button type="button" onClick={() => setPage('leave')}
            style={{ fontSize:12, color:'#4a8c60', border:'none', background:'transparent', cursor:'pointer', fontFamily:'inherit' }}>전체보기 →</button>
        </div>
        {recentLeave.length === 0 ? (
          <div style={{ padding:24, textAlign:'center', color:'#a09e99', fontSize:13 }}>내역 없음</div>
        ) : recentLeave.map(l => (
          <div key={l.id} style={{ padding:'12px 16px', borderBottom:'1px solid #f0eeea', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:13, fontWeight:600 }}>{isAdmin ? l.emp_name : l.type}</div>
              <div style={{ fontSize:11, color:'#a09e99', marginTop:2 }}>{l.start_date} ~ {l.end_date}</div>
            </div>
            <span style={{ background:statusBg(l.status), color:statusColor(l.status), padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:500 }}>
              {statusLabel(l.status)}
            </span>
          </div>
        ))}
      </div>

      {/* 최근 AS */}
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:12, overflow:'hidden' }}>
        <div style={{ padding:'14px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontWeight:700, fontSize:14 }}>최근 AS</span>
          <button type="button" onClick={() => setPage('as')}
            style={{ fontSize:12, color:'#4a8c60', border:'none', background:'transparent', cursor:'pointer', fontFamily:'inherit' }}>전체보기 →</button>
        </div>
        {recentAs.length === 0 ? (
          <div style={{ padding:24, textAlign:'center', color:'#a09e99', fontSize:13 }}>내역 없음</div>
        ) : recentAs.map(a => (
          <div key={a.id} style={{ padding:'12px 16px', borderBottom:'1px solid #f0eeea', display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:600 }}>{a.plate} · {a.customer}</div>
              <div style={{ fontSize:11, color:'#a09e99', marginTop:2, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {a.history?.[0]?.content}
              </div>
            </div>
            <span style={{ fontSize:11, color:'#a09e99', flexShrink:0 }}>{a.date}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
